import React, { useState } from 'react';
import { Send, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CareerApplicationForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    practiceArea: '',
    experienceLevel: '',
    mobilePhone: '',
    linkedinUrl: '',
    howHeard: '',
    message: '',
    privacyConfirmed: false
  });

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "File too large",
          description: "Please upload a CV under 5MB",
          variant: "destructive",
        });
        return;
      }
      if (!file.name.match(/\.(pdf|doc|docx)$/i)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF or DOC file",
          variant: "destructive",
        });
        return;
      }
      setCvFile(file);
    }
  };

  const handleRemoveFile = () => {
    setCvFile(null);
    const fileInput = document.getElementById('cv-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacyConfirmed) {
      toast({
        title: "Privacy Policy Required",
        description: "Please confirm you have read the Privacy Policy",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // For now, we'll note the CV filename but won't upload it
      // In production, you'd upload to storage first
      const applicationData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        practice_area: formData.practiceArea || null,
        experience_level: formData.experienceLevel,
        mobile_phone: formData.mobilePhone || null,
        linkedin_url: formData.linkedinUrl || null,
        how_heard: formData.howHeard,
        message: formData.message || null,
        cv_filename: cvFile ? cvFile.name : null,
        cv_url: null, // Would be set after upload
        privacy_confirmed: formData.privacyConfirmed
      };

      await axios.post(`${API_URL}/api/careers/apply`, applicationData);
      
      toast({
        title: "Application Submitted",
        description: "Thank you for your interest. We'll review your application and be in touch soon.",
      });

      // Reset form
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        practiceArea: '',
        experienceLevel: '',
        mobilePhone: '',
        linkedinUrl: '',
        howHeard: '',
        message: '',
        privacyConfirmed: false
      });
      setCvFile(null);
      const fileInput = document.getElementById('cv-upload');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Error Submitting Application",
        description: "There was a problem submitting your application. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-2 border-gray-200 shadow-xl">
      <CardContent className="p-8">
        <h3 className="text-2xl font-serif text-gray-900 mb-2">
          Would you like to build your practice with Ashbourne? Get in touch.
        </h3>
        <p className="text-gray-600 mb-6">
          Fill out the form below and we'll get back to you promptly.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="mobilePhone">Mobile Phone Number</Label>
            <Input
              id="mobilePhone"
              name="mobilePhone"
              type="tel"
              value={formData.mobilePhone}
              onChange={handleChange}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="practiceArea">What is your practice area?</Label>
            <Input
              id="practiceArea"
              name="practiceArea"
              value={formData.practiceArea}
              onChange={handleChange}
              className="mt-1"
              placeholder="e.g., Corporate Law, M&A, Litigation"
            />
          </div>

          <div>
            <Label htmlFor="experienceLevel">What level of experience are you looking to join Ashbourne at? *</Label>
            <Select value={formData.experienceLevel} onValueChange={(value) => setFormData({...formData, experienceLevel: value})} required>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select experience level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="senior-associate">Senior Associate</SelectItem>
                <SelectItem value="associate">Associate</SelectItem>
                <SelectItem value="junior-associate">Junior Associate</SelectItem>
                <SelectItem value="consultant">Consultant</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
            <Input
              id="linkedinUrl"
              name="linkedinUrl"
              type="url"
              value={formData.linkedinUrl}
              onChange={handleChange}
              className="mt-1"
              placeholder="https://linkedin.com/in/..."
            />
          </div>

          <div>
            <Label htmlFor="howHeard">How did you hear about Ashbourne? *</Label>
            <Select value={formData.howHeard} onValueChange={(value) => setFormData({...formData, howHeard: value})} required>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="legal-publication">Legal Publication</SelectItem>
                <SelectItem value="referral">Referral from colleague</SelectItem>
                <SelectItem value="recruitment-agency">Recruitment Agency</SelectItem>
                <SelectItem value="website">Website/Search Engine</SelectItem>
                <SelectItem value="event">Event/Conference</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="mt-1"
              placeholder="Tell us about your experience and why you'd like to join Ashbourne..."
            />
          </div>

          <div>
            <Label htmlFor="cv-upload">Please attach your latest CV</Label>
            <div className="mt-2">
              {cvFile ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <span className="text-sm text-gray-700 truncate flex-1">{cvFile.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    className="ml-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-gray-400 focus:outline-none">
                  <span className="flex items-center space-x-2">
                    <Upload className="h-6 w-6 text-gray-400" />
                    <span className="font-medium text-gray-600">
                      Drop your CV here or click to browse
                    </span>
                  </span>
                  <input
                    id="cv-upload"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </label>
              )}
              <p className="text-xs text-gray-500 mt-2">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="privacyConfirmed"
              name="privacyConfirmed"
              checked={formData.privacyConfirmed}
              onChange={handleChange}
              className="mt-1"
              required
            />
            <Label htmlFor="privacyConfirmed" className="text-sm font-normal cursor-pointer">
              I confirm that I have read the{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-gray-900 underline hover:text-gray-700">
                Privacy Policy
              </a>
            </Label>
          </div>

          <Button 
            type="submit" 
            size="lg" 
            className="w-full bg-gray-900 text-white hover:bg-gray-800"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
            <Send className="ml-2 h-5 w-5" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default CareerApplicationForm;